/**
 * Main API Service - Backward Compatibility Layer
 * 
 * This file maintains backward compatibility with existing code.
 * It re-exports from feature-based modules where available,
 * and keeps the old implementations for features not yet migrated.
 * 
 * NEW CODE: Import from feature modules directly:
 *   import { accountsAPI } from '../features/accounts';
 * 
 * EXISTING CODE: Continue using this file (no changes needed):
 *   import { accountsAPI } from '../services/api';
 */

// ============================================================================
// IMPORTS - All imports must be at the top
// ============================================================================
import apiClient from '../shared/services/apiClient';
import { accountsAPI as accountsAPIFeature } from '../features/accounts';
import { transactionsAPI as transactionsAPIFeature } from '../features/transactions';
import { creditorsAPI as creditorsAPIFeature } from '../features/creditors';
import { budgetsAPI as budgetsAPIFeature } from '../features/budgets';
import { categoriesAPI as categoriesAPIFeature } from '../features/categories';
import { subscriptionsAPI as subscriptionsAPIFeature } from '../features/subscriptions';
import { assetsAPI as assetsAPIFeature } from '../features/assets';
import { dashboardAPI as dashboardAPIFeature } from '../features/dashboard';
import { businessesAPI as businessesAPIFeature } from '../features/businesses';
import { tasksAPI as tasksAPIFeature } from '../features/tasks';
import { assetExpensesAPI as assetExpensesAPIFeature } from '../features/asset-expenses';
import { reconciliationAPI as reconciliationAPIFeature } from '../features/reconciliation';
import { customersAPI as customersAPIFeature } from '../features/customers';
import { vehiclesAPI as vehiclesAPIFeature } from '../features/vehicles';
import { propertiesAPI as propertiesAPIFeature } from '../features/properties';
import { accountsReceivableAPI as accountsReceivableAPIFeature } from '../features/accounts-receivable';
import { accountsPayableAPI as accountsPayableAPIFeature } from '../features/accounts-payable';
import { staffAPI as staffAPIFeature } from '../features/staff';
import { payrollAPI as payrollAPIFeature } from '../features/payroll';

// Re-export the shared client as default for backward compatibility
const api = apiClient;

// ============================================================================
// LEGACY API EXPORTS (Maintained for backward compatibility)
// ============================================================================
// These will gradually be replaced by feature-based exports above

// Businesses API - Now using feature-based module
export const businessesAPI = businessesAPIFeature;

// Accounts API - Now using feature-based module
export const accountsAPI = accountsAPIFeature;

// Transactions API - Now using feature-based module
export const transactionsAPI = transactionsAPIFeature;

// Categories API - Now using feature-based module
export const categoriesAPI = categoriesAPIFeature;

// Subscriptions API - Now using feature-based module
export const subscriptionsAPI = subscriptionsAPIFeature;

// Recurring Bills API
export const recurringBillsAPI = {
  getAll: (params) => api.get('/recurring-bills', { params }),
  getUpcoming: (params) => api.get('/recurring-bills/upcoming', { params }),
};

// Budgets API - Now using feature-based module
export const budgetsAPI = budgetsAPIFeature;

// Assets API - Now using feature-based module
export const assetsAPI = assetsAPIFeature;

// Dashboard API - Now using feature-based module
export const dashboardAPI = dashboardAPIFeature;

// Asset Expenses API - Now using feature-based module
export const assetExpensesAPI = assetExpensesAPIFeature;

// Bank Reconciliation API - Now using feature-based module
export const reconciliationAPI = reconciliationAPIFeature;

// Task Management API - Now using feature-based module
export const tasksAPI = tasksAPIFeature;

// Accounts Receivable API - Now using feature-based module
export const accountsReceivableAPI = accountsReceivableAPIFeature;

// Accounts Payable API - Now using feature-based module
export const accountsPayableAPI = accountsPayableAPIFeature;

// Customers API - Now using feature-based module
export const customersAPI = customersAPIFeature;

// Vehicles API - Now using feature-based module
export const vehiclesAPI = vehiclesAPIFeature;

// Properties API - Now using feature-based module
export const propertiesAPI = propertiesAPIFeature;

export const propertyLiabilitiesAPI = {
  getAll: (params) => api.get('/property-liabilities', { params }),
  getById: (id) => api.get(`/property-liabilities/${id}`),
  getByProperty: (propertyId) => api.get(`/property-liabilities/property/${propertyId}`),
  getUpcoming: (params) => api.get('/property-liabilities/upcoming', { params }),
  getSummary: (params) => api.get('/property-liabilities/summary', { params }),
  create: (data) => api.post('/property-liabilities', data),
  update: (id, data) => api.put(`/property-liabilities/${id}`, data),
  markPaid: (id, data) => api.post(`/property-liabilities/${id}/pay`, data),
  delete: (id) => api.delete(`/property-liabilities/${id}`),
};

// Creditors API - Now using feature-based module
export const creditorsAPI = creditorsAPIFeature;

export const liabilitiesAPI = {
  getAll: (params) => api.get('/liabilities', { params }),
  getById: (id) => api.get(`/liabilities/${id}`),
  getUpcoming: (params) => api.get('/liabilities/upcoming', { params }),
  getSummary: (params) => api.get('/liabilities/summary', { params }),
  create: (data) => api.post('/liabilities', data),
  update: (id, data) => api.put(`/liabilities/${id}`, data),
  recordPayment: (id, data) => api.post(`/liabilities/${id}/payments`, data),
  delete: (id) => api.delete(`/liabilities/${id}`),
};

// Staff API - Now using feature-based module
export const staffAPI = staffAPIFeature;

// Payroll API - Now using feature-based module
export const payrollAPI = payrollAPIFeature;

export const staffSchedulesAPI = {
  getAll: (params) => api.get('/staff-schedules', { params }),
  getById: (id) => api.get(`/staff-schedules/${id}`),
  getSummary: (params) => api.get('/staff-schedules/stats/summary', { params }),
  create: (data) => api.post('/staff-schedules', data),
  bulkCreate: (data) => api.post('/staff-schedules/bulk', data),
  update: (id, data) => api.put(`/staff-schedules/${id}`, data),
  delete: (id) => api.delete(`/staff-schedules/${id}`),
};

export const timeOffRequestsAPI = {
  getAll: (params) => api.get('/time-off-requests', { params }),
  getById: (id) => api.get(`/time-off-requests/${id}`),
  getSummary: (params) => api.get('/time-off-requests/stats/summary', { params }),
  create: (data) => api.post('/time-off-requests', data),
  update: (id, data) => api.put(`/time-off-requests/${id}`, data),
  approve: (id, data) => api.put(`/time-off-requests/${id}/approve`, data),
  deny: (id, data) => api.put(`/time-off-requests/${id}/deny`, data),
  delete: (id) => api.delete(`/time-off-requests/${id}`),
};

// Auth API (including Google OAuth)
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (userData) => api.post('/auth/register', userData),
  getProfile: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/me', data),
  changePassword: (currentPassword, newPassword) => 
    api.post('/auth/change-password', { 
      current_password: currentPassword, 
      new_password: newPassword 
    }),
  getGoogleAuthUrl: (redirect) => api.get('/auth/google', { params: { redirect } }),
  googleCallback: (code, state) => api.post('/auth/google/callback', { code, state }),
  verifyGoogleToken: (idToken) => api.post('/auth/google/verify', { idToken }),
  refresh: (refreshToken) => api.post('/auth/refresh', { refreshToken }),
  logout: () => api.post('/auth/logout'),
  getSessions: () => api.get('/auth/sessions'),
  revokeOtherSessions: () => api.post('/auth/sessions/revoke-others', {}),
  verify2FA: (pendingToken, code) => api.post('/auth/2fa/verify', { pendingToken, code }),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPasswordRequires2FA: (token) => api.post('/auth/reset-password/requires-2fa', { token }),
  resetPassword: (token, newPassword, code) => api.post('/auth/reset-password', { token, new_password: newPassword, code }),
};

// App Config API (Branding/White-label)
export const appConfigAPI = {
  get: () => api.get('/app-config'),
  update: (data) => api.put('/app-config', data),
  uploadLogo: (formData) => api.post('/app-config/upload-logo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  uploadFavicon: (formData) => api.post('/app-config/upload-favicon', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteLogo: () => api.delete('/app-config/logo'),
  resetDefaults: () => api.post('/app-config/reset-defaults'),
};

export const inventoryAPI = {
  getAll: (params) => api.get('/inventory', { params }),
  getById: (id) => api.get(`/inventory/${id}`),
  getSummary: (params) => api.get('/inventory/summary', { params }),
  create: (data) => api.post('/inventory', data),
  update: (id, data) => api.put(`/inventory/${id}`, data),
  delete: (id) => api.delete(`/inventory/${id}`),
  recordTransaction: (id, data) => api.post(`/inventory/${id}/transactions`, data),
};

export const financialReportsAPI = {
  getProfitLoss: (params) => api.get('/financial-reports/profit-loss', { params }),
  getBalanceSheet: (params) => api.get('/financial-reports/balance-sheet', { params }),
  getCashFlow: (params) => api.get('/financial-reports/cash-flow', { params }),
  getDivisions: (params) => api.get('/financial-reports/divisions', { params }),
};

export const booksDownloadAPI = {
  download: (list) => api.get(`/books-download/${list}`, { responseType: 'blob' }),
};

export const taxExportAPI = {
  export: (params) => api.get('/tax-export/export', { params, responseType: 'blob' }),
  getSummary: (params) => api.get('/tax-export/summary', { params }),
};

export const form1099API = {
  getEligible: (params) => api.get('/form1099/eligible', { params }),
  generate: (vendorId, params) => api.get(`/form1099/generate/${vendorId}`, { params, responseType: 'blob' }),
  getVendorPayments: (vendorId, params) => api.get(`/form1099/vendor/${vendorId}`, { params }),
};

export const auditAPI = {
  getTransactions: (params) => api.get('/audit/transactions', { params }),
  getUsers: () => api.get('/audit/users'),
};

export const pageVisibilityAPI = {
  getPages: () => api.get('/page-visibility/pages'),
  getMyPages: () => api.get('/page-visibility/my-pages'),
  getSubordinates: () => api.get('/page-visibility/subordinates'),
  getUserPages: (userId) => api.get(`/page-visibility/user/${userId}`),
  updateUserPages: (userId, pages) => api.put(`/page-visibility/user/${userId}`, { pages }),
};

export const twoFactorAPI = {
  getStatus: () => api.get('/2fa/status'),
  totpSetup: () => api.post('/2fa/totp/setup'),
  totpConfirm: (code) => api.post('/2fa/totp/confirm', { code }),
  totpDisable: (password) => api.post('/2fa/totp/disable', { password }),
  emailSetup: () => api.post('/2fa/email/setup'),
  emailConfirm: (code) => api.post('/2fa/email/confirm', { code }),
  emailDisable: (password) => api.post('/2fa/email/disable', { password }),
  regenerateRecoveryCodes: (password) => api.post('/2fa/recovery-codes/regenerate', { password }),
};

export default api;
